/**
 * Single source of truth for usage credits. Deliberately dependency-free:
 * browser bundles (marketing, frontend, shared-ui) import via
 * `@sc/stripe/pricing` so they never touch the Stripe SDK / Prisma barrel.
 *
 * Model:
 * - 1 credit = 0.1p = £0.001 (buy and debit share the same unit)
 * - Ascend applies any markup when it bills a generation. Starkcrawl does not
 * - Firecrawl (platform key): marketed at 1 credit per Firecrawl credit
 *   (FIRECRAWL_BASE_CREDITS). Debit is not wired yet; recorded on the capture.
 *   A project Firecrawl key is billed on the customer's Firecrawl account.
 * - Extraction, hygiene, remediation and search terms: Ascend bills the
 *   model cost. USAGE_CREDIT_RANGES is the typical band shown in the UI.
 *   The Low / Medium / High base constants below are the old flat guides.
 * - Category reassess (Requesty Jev): marketed at REQUESTY_JEV_BASE_CREDITS
 *   per call. Debit is not wired yet. A Requesty key skips that credit.
 * - Topic hygiene run: HYGIENE_BASE_CREDITS
 * - Remediation run: REMEDIATION_BASE_CREDITS
 * - Search-term generation: SEARCH_TERM_CREDITS per term
 * - AWS compute: platformCredits = vendorGbp / GBP_PER_CREDIT
 * - BYO Firecrawl key: billed on the customer's Firecrawl account
 */

/** GBP value of one platform credit (0.1p). £1000 = 1_000_000 credits. */
export const GBP_PER_CREDIT = 0.001;

/** Platform credits per Firecrawl vendor credit. */
export const FIRECRAWL_BASE_CREDITS = 1;

/** Platform credits per Low classification / recategorise call. */
export const BEDROCK_CLASSIFY_LOW_BASE_CREDITS = 1;

/** Platform credits per Medium classification / recategorise call. */
export const BEDROCK_CLASSIFY_MEDIUM_BASE_CREDITS = 4;

/**
 * Platform credits per High classification / recategorise call.
 * Stubborn bake-off High = Opus 4.7; Low base is 1.
 */
export const BEDROCK_CLASSIFY_HIGH_BASE_CREDITS = 40;

/**
 * Fixed platform credits per Jev (Requesty) category reassess call.
 */
export const REQUESTY_JEV_BASE_CREDITS = 1;

/** @deprecated Prefer BEDROCK_CLASSIFY_LOW_BASE_CREDITS — alias for marketing/default Low. */
export const BEDROCK_CLASSIFY_BASE_CREDITS = BEDROCK_CLASSIFY_LOW_BASE_CREDITS;

/** Platform credits per topic hygiene run. */
export const HYGIENE_BASE_CREDITS = 10;

/** Platform credits per remediation run. */
export const REMEDIATION_BASE_CREDITS = 50;

/**
 * Fixed platform credits per generated search term (topic discovery / regen).
 */
export const SEARCH_TERM_CREDITS = 1;

/** Crawl cost shown in marketing and the billing UI. */
export const DEFAULT_CRAWL_CREDITS = FIRECRAWL_BASE_CREDITS;

/** Low classification cost shown in marketing and the billing UI. */
export const DEFAULT_CLASSIFY_CREDITS = BEDROCK_CLASSIFY_LOW_BASE_CREDITS;

/** Medium classification cost shown in marketing and the billing UI. */
export const DEFAULT_CLASSIFY_MEDIUM_CREDITS = BEDROCK_CLASSIFY_MEDIUM_BASE_CREDITS;

/** High classification cost shown in marketing and the billing UI. */
export const DEFAULT_CLASSIFY_HIGH_CREDITS = BEDROCK_CLASSIFY_HIGH_BASE_CREDITS;

/** Topic hygiene run cost shown in marketing and the FAQ. */
export const DEFAULT_HYGIENE_CREDITS = HYGIENE_BASE_CREDITS;

/** Remediation run cost shown in marketing and the FAQ. */
export const DEFAULT_REMEDIATION_CREDITS = REMEDIATION_BASE_CREDITS;

/** Default search terms requested per category. */
export const DEFAULT_TERMS_PER_CATEGORY = 10;

/** Max search terms per category on discover / regen. */
export const MAX_TERMS_PER_CATEGORY = 20;

/**
 * Stamped on every Starkcrawl Stripe subscription. The webhook ignores
 * objects whose `product` metadata is set to anything else (shared Stark
 * Stripe account also bills Starkchat).
 */
export const STARKCRAWL_STRIPE_PRODUCT = 'starkcrawl';

export const withStarkcrawlStripeMetadata = (
  metadata: Record<string, string>
): Record<string, string> => ({
  product: STARKCRAWL_STRIPE_PRODUCT,
  ...metadata,
});

/** Max concurrent category drains per project (admission fair-share ceiling). */
export const PER_PROJECT_CONCURRENT_CATEGORY_DRAINS = 3;

/**
 * Marketing / display org concurrent category-drain cap.
 * Runtime admission uses drain-admission env caps (single default).
 */
export const ORG_CONCURRENT_CATEGORY_DRAINS = 100;

/** Clamp terms-per-category to [1, MAX_TERMS_PER_CATEGORY]. */
export const clampTermsPerCategory = (raw: unknown): number => {
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) return DEFAULT_TERMS_PER_CATEGORY;
  return Math.min(
    MAX_TERMS_PER_CATEGORY,
    Math.max(1, Math.round(n))
  );
};

/** Credits granted for a GBP spend at the fixed 0.1p/credit rate. */
export const creditsForSpendGbp = (priceGbp: number): number => {
  if (priceGbp <= 0) return 0;
  return Math.round(priceGbp / GBP_PER_CREDIT);
};

/** Firecrawl vendor units → platform credits. */
export const firecrawlCreditsToPlatformCredits = ({
  credits,
}: {
  credits: number;
}): number => {
  if (credits <= 0) return 0;

  return credits * FIRECRAWL_BASE_CREDITS;
};

/** Classification call → platform credits (default Low). */
export const classifyCreditsToPlatformCredits = (
  effort: 'high' | 'jev' | 'low' | 'medium' = 'low'
): number => {
  if (effort === 'jev') return REQUESTY_JEV_BASE_CREDITS;

  if (effort === 'high') return BEDROCK_CLASSIFY_HIGH_BASE_CREDITS;

  if (effort === 'medium') return BEDROCK_CLASSIFY_MEDIUM_BASE_CREDITS;

  return BEDROCK_CLASSIFY_LOW_BASE_CREDITS;
};

/** Fixed platform credits per inferred-category reassess (Jev). */
export const recategoriseCreditsToPlatformCredits = (
  captureCount = 1
): number => {
  if (!Number.isFinite(captureCount) || captureCount <= 0) return 0;
  return Math.round(captureCount) * REQUESTY_JEV_BASE_CREDITS;
};

/**
 * Typical credits shown in marketing and billing. A longer page costs more.
 * Topic hygiene, remediation and search terms use the same models as
 * extractions. Platform-key crawl is 1 credit per Firecrawl credit; a project
 * Firecrawl key is billed on the customer's Firecrawl account and is not a
 * Starkcrawl credit. Reclassify is 1 credit per call on the platform key; a
 * Requesty key skips that credit.
 */
export interface UsageCreditRange {
  action: string;
  credits: string;
}

export const USAGE_CREDIT_RANGES: readonly UsageCreditRange[] = [
  { action: 'Extraction Low', credits: 'About 1' },
  { action: 'Extraction Medium', credits: '3–14' },
  { action: 'Extraction High', credits: 'About 15–140' },
  {
    action: 'Crawl',
    credits:
      '1 credit per Firecrawl credit on the platform key. A project Firecrawl key is billed on your Firecrawl account and is not a Starkcrawl credit.',
  },
  {
    action: 'Reclassify',
    credits:
      '1 credit per call on the platform key. A Requesty key on the project skips that credit and is billed on your Requesty account using Jev model.',
  },
];

export const USAGE_CREDIT_RANGE_NOTE =
  'These are typical credits at the standard rate. A longer page costs more. Topic hygiene, remediation and search terms use the same models as extractions, so they land in the same ranges.';

/**
 * Short extraction ranges for plan copy. The in-app classify-effort dropdown
 * uses classifyEffortOptionLabel and keeps saying Classify.
 */
export const classifyEffortCreditBreakdown = (): string =>
  'Low ~1, Medium 3–14, High ~15–140';

/** Extraction breakdown for marketing and billing copy. */
export const CLASSIFY_EFFORT_CREDIT_BREAKDOWN = classifyEffortCreditBreakdown();

/** Dropdown / ListBox label with the typical credit range in brackets. */
export const classifyEffortOptionLabel = (
  effort: 'high' | 'low' | 'medium'
): string => {
  if (effort === 'high') return 'High (15–140 credits)';

  if (effort === 'medium') return 'Medium (3–14 credits)';

  return 'Low (~1 credit)';
};

/** Topic hygiene run → platform credits. */
export const hygieneCreditsToPlatformCredits = (): number => HYGIENE_BASE_CREDITS;

/** Remediation run → platform credits. */
export const remediationCreditsToPlatformCredits = (): number => REMEDIATION_BASE_CREDITS;

/** Search terms → platform credits. */
export const searchTermsToPlatformCredits = (termCount: number): number => {
  if (termCount <= 0) return 0;
  return Math.round(termCount) * SEARCH_TERM_CREDITS;
};

/** Vendor GBP → platform credits. platformCredits = vendorGbp / GBP_PER_CREDIT. */
export const vendorGbpToPlatformCredits = (rawGbp: number): number => {
  if (!Number.isFinite(rawGbp) || rawGbp <= 0) return 0;

  return rawGbp / GBP_PER_CREDIT;
};

/**
 * Prepaid credit pack steps (GBP). Grant = spend ÷ GBP_PER_CREDIT.
 * e.g. £75 → 75_000 credits; £1000 → 1_000_000 credits.
 */
export interface UsageCreditStepInterface {
  priceGbp: number;
}

export const USAGE_CREDIT_STEPS: readonly UsageCreditStepInterface[] = [
  { priceGbp: 75 },
  { priceGbp: 150 },
  { priceGbp: 300 },
  { priceGbp: 500 },
  { priceGbp: 750 },
  { priceGbp: 1_000 },
];

/** Rough Low classifications a pack buys. */
export const roughClassificationsForCredits = (platformCredits: number): number => {
  const per = classifyCreditsToPlatformCredits();

  if (platformCredits <= 0 || per <= 0) return 0;

  return Math.floor(platformCredits / per);
};

/** Rough platform-key crawls a pack buys. */
export const roughCrawlsForCredits = (platformCredits: number): number => {
  const per = firecrawlCreditsToPlatformCredits({ credits: 1 });

  if (platformCredits <= 0 || per <= 0) return 0;

  return Math.floor(platformCredits / per);
};

export const usageIllustration = (priceGbp: number) => {
  const credits = creditsForSpendGbp(priceGbp);

  return {
    credits,
    classifications: roughClassificationsForCredits(credits),
    crawls: roughCrawlsForCredits(credits),
    pricePerCredit: GBP_PER_CREDIT,
    classifyCredits: classifyCreditsToPlatformCredits(),
    crawlCredits: firecrawlCreditsToPlatformCredits({ credits: 1 }),
  };
};

/** Stripe lookup key for a one-time usage credit pack, e.g. starkcrawl_credits_75. */
export const usageCreditPriceLookupKey = (
  step: UsageCreditStepInterface
): string => `starkcrawl_credits_${step.priceGbp}`;

/** Stripe lookup key for a monthly credit subscription, e.g. starkcrawl_credits_monthly_75. */
export const usageCreditMonthlyPriceLookupKey = (
  step: UsageCreditStepInterface
): string => `starkcrawl_credits_monthly_${step.priceGbp}`;

/** "£112.50" for fractional prices, "£75" for whole-pound ones. */
export const formatPriceGbp = (value: number): string =>
  `£${value.toLocaleString('en-GB', {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;

/** Display-only: GBP price as pence (0.10p). */
export const formatPricePerCredit = (
  value: number = GBP_PER_CREDIT
): string => {
  const pence = value * 100;

  return `${pence.toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}p`;
};

/**
 * Free-trial grant at signup, in credits.
 * 3_000 credits = £3 at 0.1p/credit.
 */
export const TRIAL_ALLOWANCE_CREDITS = 3_000;

/** Label for credit-pack / subscription emails and UI. */
export const planDisplayName = (): string => 'Credits';

export const stepIndexForSubtotal = (subtotal: number): number | null => {
  const index = USAGE_CREDIT_STEPS.findIndex(
    (step) => step.priceGbp === subtotal
  );

  return index === -1 ? null : index;
};
